import dayjs from 'dayjs';
import difference from 'lodash/difference';
import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import omitBy from 'lodash/omitBy';
import sortBy from 'lodash/sortBy';
import union from 'lodash/union';

import { stripLocationObject } from '@/app/socialListening/composables/useLocationOptions';
import { SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS } from '@/app/socialListening/constants';
import {
  CUSTOM_DATE_FILTER_OPTION,
  dateTimeFormat,
  LISTENING_SCOPED_FILTERS,
  ROLLING_DATE_RANGE_OFFSET_MAP,
  SENTIMENT_FILTER_OPTIONS,
  TOPIC_DATE_FILTER_OPTIONS,
} from '@/app/socialListening/constants/listeningFilters';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';
import { lastDateRange } from '@/utils/dateUtils';
import { logger } from '@/utils/logger';

export function getDateRangeByOffset(offset, dateService) {
  const filterOffsetMap = invert(ROLLING_DATE_RANGE_OFFSET_MAP);
  const preset = filterOffsetMap[offset] ?? TOPIC_DATE_FILTER_OPTIONS.LAST_4_WEEKS.value;
  const presetObject = TOPIC_DATE_FILTER_OPTIONS[preset];
  const { start, end } = lastDateRange(
    presetObject.subtractValue,
    presetObject.subtractUnit,
    dateService,
  );
  return {
    start: dayjs(start).format(dateTimeFormat),
    end: dayjs(end).format(dateTimeFormat),
  };
}

export function formatDateRange(start, end, offset) {
  const dateRange = {
    onOrAfter: dayjs(start).format(dateTimeFormat),
    onOrBefore: dayjs(end).format(dateTimeFormat),
  };
  if (offset) {
    dateRange.rollingDateRangeOffset = offset;
  }
  return dateRange;
}

export function sortFilterOptionsByLabel(options, additionalFields = []) {
  const fields = [(o) => o?.label.toLowerCase(), ...additionalFields];
  return sortBy(options, fields);
}

export function convertToScopedTopicFilters({
  sources,
  sourceCreated,
  sentiment,
  mediaTypes,
  mentionTypes,
  visualData,
  industryIds,
  sourceAndCreatorIds,
  locations,
}) {
  const filters = {
    sources: sources?.length ? sources : undefined,
    industryIds: industryIds?.length ? industryIds : undefined,
    sourceAndCreatorIds: sourceAndCreatorIds?.length ? sourceAndCreatorIds : undefined,
    visualData: !isEmpty(visualData) ? visualData : undefined,
    locations: locations?.length ? locations : undefined,
  };

  /**
   * sentiment: { includeNegative: Boolean, includeNeutral: Boolean, includePositive: Boolean }
   *
   * Expected value example
   * sentiment: ['NEGATIVE', 'NEUTRAL', 'POSITIVE']
   */
  if (!isEmpty(sentiment)) {
    const value = [];
    Object.values(SENTIMENT_FILTER_OPTIONS).forEach((v) => {
      if (sentiment[v.field]) {
        value.push(v.value);
      }
    });
    filters.sentiment = value;
  } else {
    filters.sentiment = undefined;
  }

  /**
   * sourceCreated: { onOrAfter: Datetime, onOrBefore: Datetime, rollingDateRangeOffset: Integer }
   *
   * Expected value example
   * sourceCreated: {
   *    preset: 'CUSTOM',
   *    presetObject: { label: 'CUSTOM', label: 'Custom' },
   *    range: { start: Datetime, end: Datetime }
   * }
   */
  if (!isEmpty(sourceCreated)) {
    let value;
    const filterOffsetMap = invert(ROLLING_DATE_RANGE_OFFSET_MAP);
    const offset = sourceCreated?.rollingDateRangeOffset;

    if (offset && filterOffsetMap[offset]) {
      const preset = filterOffsetMap[offset];
      value = { preset };
    } else {
      value = {
        preset: CUSTOM_DATE_FILTER_OPTION.value,
        range: {
          start: dayjs(sourceCreated.onOrAfter).startOf('day').toDate(),
          end: dayjs(sourceCreated.onOrBefore).endOf('day').toDate(),
        },
      };
    }
    filters.sourceCreated = value;
  } else {
    filters.sourceCreated = undefined;
  }

  /**
   * mediaTypes: { includes: Array, doesNotInclude: Array }
   *
   * Expected value example
   * mediaTypes: ['IMAGE', 'VIDEO', 'LINK', 'TEXT']
   */
  if (!isEmpty(mediaTypes) && mediaTypes?.includes?.length) {
    filters.mediaTypes = [...mediaTypes.includes];
  } else {
    filters.mediaTypes = undefined;
  }

  /**
   * mentionTypes: { includes: Array, doesNotInclude: Array }
   *
   * Expected value example
   * mentionTypes: ['BLOGS', 'CAPTION', 'DISCUSSIONS', 'NEWS']
   */
  if (!isEmpty(mentionTypes) && mentionTypes?.includes?.length) {
    filters.mentionTypes = [...mentionTypes.includes];
  } else {
    filters.mentionTypes = undefined;
  }

  return { ...LISTENING_SCOPED_FILTERS.TOPIC.default, ...omitBy(filters, isEmpty) };
}

export function convertLoadedQueryParamsToScopedFilters(payload) {
  // wrap media types in includes for convertToScopedTopicFilters
  const { mediaTypes } = payload;
  if (Array.isArray(mediaTypes) && mediaTypes.length) {
    payload.mediaTypes = { includes: mediaTypes };
  }
  return convertToScopedTopicFilters(payload);
}

export function convertSentimentFilter(sentiment) {
  if (!sentiment?.length) return undefined;
  const value = {};
  Object.values(SENTIMENT_FILTER_OPTIONS).forEach((v) => {
    value[v.field] = sentiment.includes(v.value);
  });
  return value;
}

export function convertSourceCreatedFilter(sourceCreated, dateService) {
  if (!sourceCreated?.preset && !sourceCreated?.presetObject && !sourceCreated?.range) {
    return undefined;
  }

  if (sourceCreated.range) {
    return {
      onOrAfter: dayjs(sourceCreated.range.start).format(dateTimeFormat),
      onOrBefore: dayjs(sourceCreated.range.end).format(dateTimeFormat),
    };
  }

  const presetObject =
    sourceCreated.presetObject ?? TOPIC_DATE_FILTER_OPTIONS[sourceCreated.preset];
  const offset = ROLLING_DATE_RANGE_OFFSET_MAP[sourceCreated.preset];
  const { start, end } = lastDateRange(
    presetObject.subtractValue,
    presetObject.subtractUnit,
    dateService,
  );

  return {
    onOrAfter: dayjs(start).format(dateTimeFormat),
    onOrBefore: dayjs(end).format(dateTimeFormat),
    ...(offset && { rollingDateRangeOffset: offset }),
  };
}

export function convertLocationsFilter(locations) {
  if (!locations?.length) return undefined;
  return locations.map(stripLocationObject).filter((location) => typeof location !== 'string');
}

export function convertToSearchBodyFilters({
  sources,
  sourceCreated,
  sentiment,
  mediaTypes,
  mentionTypes,
  visualData,
  industryIds,
  sourceAndCreatorIds,
  locations,
  topicId,
  dateService,
}) {
  return {
    topicId,
    sources: sources?.length ? sources : undefined,
    industryIds: industryIds?.length ? industryIds : undefined,
    sourceAndCreatorIds: sourceAndCreatorIds?.length ? sourceAndCreatorIds : undefined,
    mediaTypes: mediaTypes?.length ? { includes: mediaTypes } : undefined,
    mentionTypes: mentionTypes?.length ? { includes: mentionTypes } : undefined,
    locations: convertLocationsFilter(locations),
    sentiment: convertSentimentFilter(sentiment),
    sourceCreated: convertSourceCreatedFilter(sourceCreated, dateService),
    visualData:
      visualData?.includes?.length || visualData?.doesNotInclude?.length ? visualData : undefined,
  };
}

export function formatVisualFilters(_filters) {
  // Formats visual filters for refined results queries
  // Exclude TEXT, LINK from refined results, since they cannot produce visual filter results
  const mediaFilterExcludes = [
    SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.LINK.value,
    SOCIAL_LISTENING_MEDIA_TYPE_OPTIONS.TEXT.value,
  ];
  const excludes = union(_filters.mediaTypes?.doesNotInclude || [], mediaFilterExcludes);
  if (!_filters.mediaTypes) {
    _filters.mediaTypes = {};
  }
  _filters.mediaTypes.doesNotInclude = excludes;
  if (_filters?.mediaTypes?.includes) {
    // Simplify includes since we don't want to include & exclude the same things
    _filters.mediaTypes.includes = difference(_filters.mediaTypes.includes, excludes);
  }
}

export function sortVisualFilterArrays(obj) {
  return {
    ...obj,
    doesNotInclude: sortBy(obj.doesNotInclude ?? [], JSON.stringify),
    includes: sortBy(obj.includes ?? [], JSON.stringify),
  };
}

export function convertToTrendsFilters(searchBodyFilters) {
  const { sources, mediaTypes, ...filterData } = searchBodyFilters ?? {};
  return {
    ...omitBy(
      {
        channels: sources,
        mediaTypes: Array.isArray(mediaTypes?.includes) ? mediaTypes.includes : mediaTypes,
        ...filterData,
      },
      isEmpty,
    ),
  };
}

export function getFiltersFromStorage(scope, id) {
  try {
    const data = browserStorageGetItem(`${scope}Filters-${id}`);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

export function setFiltersToStorage(scope, id, filterData) {
  try {
    browserStorageSetItem(`${scope}Filters-${id}`, JSON.stringify(filterData));
  } catch (error) {
    logger.warn('Failed to save topic filters to localStorage', {}, error);
  }
}
